import { useEffect, useState } from 'react';

import { isModalPresent } from 'js/utils/modalPresence';

// Desktop exit-intent trigger: fires once per session when the pointer leaves through the top of
// the viewport. Split out from the modal so the trigger contract can be tested without rendering.
//
// Ported from the previous vanilla-TS implementation; the heuristic and the session guard are
// unchanged. What is gone is everything the DS Modal now owns (visibility toggling, focus trap,
// Escape, backdrop).

const EXIT_SHOWN_KEY = 'loswpExitIntentShown';

const sessionFlag = (key: string): boolean => {
  try {
    return window.sessionStorage.getItem(key) === '1';
  } catch {
    return true; // no sessionStorage (privacy mode) → behave as already shown
  }
};

const setSessionFlag = (key: string): void => {
  try {
    window.sessionStorage.setItem(key, '1');
  } catch {
    // ignore — worst case the prompt stays suppressed
  }
};

type UseExitIntent = {
  triggered: boolean;
  reset: () => void;
};

export function useExitIntent(): UseExitIntent {
  const [triggered, setTriggered] = useState(false);

  useEffect(() => {
    if (sessionFlag(EXIT_SHOWN_KEY)) return undefined;

    const onMouseOut = (event: MouseEvent): void => {
      // Fire only when the pointer leaves through the top of the viewport (toward the
      // URL bar / tabs) — the conventional exit-intent heuristic, desktop-only by nature.
      if (event.relatedTarget || event.clientY > 0) return;

      // The login modal and the interruption cascade also run on this page. Don't stack on an
      // open dialog — and don't queue behind it either: a "before you go" that surfaces minutes
      // later, after some other flow finishes, is worse than not showing at all. Keep listening
      // so a later mouse-leave can still land.
      if (isModalPresent()) return;

      document.removeEventListener('mouseout', onMouseOut);
      setSessionFlag(EXIT_SHOWN_KEY);
      setTriggered(true);
    };

    document.addEventListener('mouseout', onMouseOut);
    return () => document.removeEventListener('mouseout', onMouseOut);
  }, []);

  // Closing the modal only clears the render state; the session flag stays set, so it cannot
  // re-trigger on this tab.
  return { triggered, reset: () => setTriggered(false) };
}
